if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.onPageShow = this.onPageShow.bind(this);
      }

      connectedCallback() {
        window.addEventListener('pageshow', this.onPageShow);
      }

      disconnectedCallback() {
        window.removeEventListener('pageshow', this.onPageShow);
      }

      onPageShow(event) {
        if (!event.persisted || !this.cartRedirecting) return;
        this.cartRedirecting = false;
        if (!this.error) this.setSubmitButtonState({ ariaDisabled: null });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (!this.form.reportValidity()) return;

        this.error = false;
        this.handleErrorMessage();

        this.setSubmitButtonState({ ariaDisabled: 'true' });
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const result = await response.json();
            if (!response.ok && !result.status) throw new Error(result.description || result.message || 'Add to cart failed');
            return result;
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.setSubmitButtonState({ ariaDisabled: 'true' });
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            this.handleConfirmedAdd(response, variantId, linesUpdateDeferred);
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings.error);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error && !this.cartRedirecting) this.setSubmitButtonState({ ariaDisabled: null });
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleConfirmedAdd(response, variantId, linesUpdateDeferred) {
        // Cart mutation has succeeded. A display or subscriber failure must not
        // show a failed-add message and invite the shopper to add the item again.
        try {
          this.resolveCartLinesUpdate(linesUpdateDeferred);
          if (!this.cart) {
            this.redirectToCart();
            return;
          }

          try {
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: response,
            })).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            }).catch(() => {});
          } catch {}

          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => this.renderConfirmedCart(response));
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.renderConfirmedCart(response);
          }
        } catch {
          this.redirectToCart();
        }
      }

      renderConfirmedCart(response) {
        try {
          CartPerformance.measure('add:paint-updated-sections', () => this.cart.renderContents(response));
        } catch {
          this.redirectToCart();
        }
      }

      redirectToCart() {
        this.cartRedirecting = true;
        this.setSubmitButtonState({ ariaDisabled: 'true' });
        window.location = window.routes.cart_url;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      setSubmitButtonState(values) {
        if (window.RegionalRestriction?.setControlState) {
          window.RegionalRestriction.setControlState(this.submitButton, values);
          return;
        }
        if ('disabled' in values) this.submitButton.disabled = values.disabled;
        if ('ariaDisabled' in values) {
          if (values.ariaDisabled === null) this.submitButton.removeAttribute('aria-disabled');
          else this.submitButton.setAttribute('aria-disabled', values.ariaDisabled);
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.setSubmitButtonState({ disabled: disable });
        if (disable) {
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
